import { useState, useEffect, useMemo } from "react";
import {
  X,
  Trash2,
  Layers,
  ImageIcon,
  Merge,
  ChevronUp,
  ChevronDown,
  EyeOff,
  CircleHelp,
} from "lucide-react";
import { useConfigStore } from "../store/configStore";
import { useEditorStore } from "../store/editorStore";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import useSWR from "swr";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { LayerIconPicker } from "./LayerIconPicker";
import { layerIcon } from "commons/cdn";
import { MergeSelectionDialog } from "./MergeSelectionDialog";
import {
  getSelectionRequireCategories,
  isSelectionLayerRequired,
} from "../lib/selectionUtils";

export function SelectionDetailPanel() {
  const { templateConfig } = useConfigStore();
  const {
    updateSelectionName,
    updateSelectionLayerIcon,
    updateSelectionGid,
    updateSelectionBackground,
    updateSelectionPlayIndex,
    updateSelectionHidden,
    deleteSelection,
    editingSelectionId,
    setEditingSelectionId,
    selectedSkins,
    setSelectionRequired,
  } = useEditorStore();
  const { data: layerGroups = [] } = useSWR(["/layer-groups"]);
  const visibleLayerGroups = useMemo(
    () => (layerGroups as any[]).filter((group: any) => group.state !== -1),
    [layerGroups],
  );

  const selection = templateConfig?.selection.find(
    (s: any) => s.id === editingSelectionId
  );

  const [name, setName] = useState(selection?.name || "");
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);

  // 当 selection 变化时更新本地状态
  useEffect(() => {
    if (selection) {
      setName(selection.name || "");
    }
  }, [selection]);

  // 计算排序后的 selection 列表和当前位置
  const sortedSelections = useMemo(() => {
    if (!templateConfig?.selection) return [];
    return [...templateConfig.selection].sort(
      (a, b) => a.playIndex - b.playIndex
    );
  }, [templateConfig]);

  const currentSortedIndex = sortedSelections.findIndex(
    (s) => s.id === selection?.id
  );

  if (!selection) {
    return (
      <Card className="h-full flex flex-col">
        <CardHeader className="pb-2 flex-shrink-0">
          <CardTitle className="text-lg">Selection 详情</CardTitle>
        </CardHeader>
        <CardContent className="flex-1 flex items-center justify-center">
          <p className="text-sm text-muted-foreground text-center">
            点击 Selection 的「详情」按钮查看
          </p>
        </CardContent>
      </Card>
    );
  }

  const skuIds = selection.skuIds || [];
  const skuCount = skuIds.length;

  // 计算装载数量（当前选中的皮肤数量）
  const skus = templateConfig?.skus || {};
  const selectedCount = skuIds.filter((skuId: string) => {
    const sku = skus[skuId];
    return sku && selectedSkins.includes(sku.skinName);
  }).length;

  const handleNameBlur = () => {
    if (name !== selection.name) {
      updateSelectionName(selection.id, name);
    }
  };

  const handleDelete = () => {
    if (skuCount > 0) {
      // 有 SKU 时不允许删除
      return;
    }
    deleteSelection(selection.id);
    setEditingSelectionId(null);
  };

  const requireCategoriesForSelection = getSelectionRequireCategories(
    selection,
    skus,
  );
  const isRequired = isSelectionLayerRequired(selection);

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="pb-2 flex-shrink-0">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Layers className="h-4 w-4 text-primary" />
            Selection
            <span className="text-sm font-mono text-muted-foreground">
              {selection.id}
            </span>
          </CardTitle>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={() => setEditingSelectionId(null)}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="flex-1 overflow-auto space-y-4">
        {/* LayerIcon - 顶部大图 */}
        <div className="space-y-2">
          <Label className="text-xs">图标</Label>
          <LayerIconPicker
            currentLayerIconId={selection.layerIconId}
            onSelect={(icon) => {
              if (icon) {
                updateSelectionLayerIcon(selection.id, icon);
              }
            }}
          >
            <div className="w-full aspect-square max-w-[160px] mx-auto rounded-lg border-2 border-dashed border-muted-foreground/30 flex items-center justify-center cursor-pointer hover:border-primary hover:bg-muted/30 transition-all">
              {selection.layerIconId ? (
                <img
                  src={layerIcon(selection.layerIconId)}
                  alt="LayerIcon"
                  className="w-full h-full object-contain p-2"
                />
              ) : (
                <div className="flex flex-col items-center gap-2 text-muted-foreground">
                  <ImageIcon className="h-10 w-10" />
                  <span className="text-xs">点击选择图标</span>
                </div>
              )}
            </div>
          </LayerIconPicker>
          {selection.layerIconId && (
            <p className="text-[10px] font-mono text-muted-foreground text-center truncate">
              {selection.layerIconId}
            </p>
          )}
        </div>

        {/* 名称编辑 */}
        <div className="space-y-1.5">
          <Label className="text-xs">名称</Label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={handleNameBlur}
            placeholder="Selection 名称"
            className="text-sm"
          />
        </div>

        {/* Bind group */}
        <div className="space-y-1.5">
          <Label className="text-xs">Bind group</Label>
          <Select
            value={selection.gid || "__none__"}
            onValueChange={(value) =>
              updateSelectionGid(
                selection.id,
                value === "__none__" ? "" : String(value),
              )
            }
          >
            <SelectTrigger className="text-sm">
              <SelectValue placeholder="选择大类" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">不设置</SelectItem>
              {visibleLayerGroups.map((group: any) => (
                <SelectItem key={group.id} value={group.id}>
                  {`${group.name || group.id} - ${group.id}`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* 必选：层 opt + 联动 requireCategories */}
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label className="text-xs">必选层</Label>
            <p className="text-[10px] text-muted-foreground">
              {requireCategoriesForSelection.length > 0
                ? `层 opt + category: ${requireCategoriesForSelection.join(", ")}`
                : "写入层 opt；有 SKU 后会联动 Skin 分类必选"}
            </p>
          </div>
          <Switch
            checked={isRequired}
            onCheckedChange={(value) => {
              setSelectionRequired(selection.id, value);
            }}
          />
        </div>

        {/* 统计 + 隐藏开关 */}
        <div className="flex items-center justify-between">
          <Badge variant={selectedCount > 0 ? "default" : "secondary"}>
            装载 {selectedCount}/{skuCount}个 SKU
          </Badge>
          <div className="flex items-center gap-2">
            <EyeOff className="h-3.5 w-3.5 text-muted-foreground" />
            <Switch
              checked={selection.hidden || false}
              onCheckedChange={(checked) =>
                updateSelectionHidden(selection.id, checked)
              }
            />
            <Label className="text-xs text-muted-foreground">隐藏</Label>
          </div>
        </div>

        {/* Background 标识（与普通模版 layer 一致：1 表示开启） */}
        <div className="space-y-1.5">
          <div className="flex items-center gap-1">
            <Label className="text-xs">Background</Label>
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="text-muted-foreground hover:text-foreground"
                >
                  <CircleHelp className="h-3.5 w-3.5" />
                </button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-72 p-3">
                <p className="text-xs leading-5">
                  标记层为背景层{" "}
                  <a
                    href="https://stickerstyle.feishu.cn/docx/CffJdaRkvo8yadxHjy4c4wbQnud"
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary underline underline-offset-2"
                  >
                    查看详情
                  </a>
                </p>
              </PopoverContent>
            </Popover>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="selection-background"
              checked={selection.background === 1}
              onCheckedChange={(checked) =>
                updateSelectionBackground(selection.id, checked ? 1 : 0)
              }
            />
            <Label htmlFor="selection-background" className="text-xs">
              is background
            </Label>
          </div>
        </div>

        {/* PlayIndex 调整 */}
        <div className="space-y-1.5">
          <Label className="text-xs">显示顺序</Label>
          <div className="flex items-center gap-2">
            <div className="flex-1 h-9 px-3 rounded-md border bg-muted/50 flex items-center justify-center">
              <span className="text-sm font-mono">
                第 {selection.playIndex + 1} 位 / 共 {sortedSelections.length}{" "}
                个
              </span>
            </div>
            <Button
              variant="outline"
              size="icon"
              className="h-9 w-9"
              disabled={currentSortedIndex <= 0}
              onClick={() =>
                updateSelectionPlayIndex(selection.id, selection.playIndex - 1)
              }
              title="上移"
            >
              <ChevronUp className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-9 w-9"
              disabled={currentSortedIndex >= sortedSelections.length - 1}
              onClick={() =>
                updateSelectionPlayIndex(selection.id, selection.playIndex + 1)
              }
              title="下移"
            >
              <ChevronDown className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* 合并 & 删除按钮 */}
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            className="flex-1"
            onClick={() => setMergeDialogOpen(true)}
            disabled={skuCount === 0}
          >
            <Merge className="h-3 w-3 mr-1" />
            合并
          </Button>
          <Button
            variant="destructive"
            size="sm"
            className="flex-1"
            onClick={handleDelete}
            disabled={skuCount > 0}
          >
            <Trash2 className="h-3 w-3 mr-1" />
            删除
          </Button>
        </div>
      </CardContent>

      {/* 合并 Selection 弹窗 */}
      <MergeSelectionDialog
        open={mergeDialogOpen}
        onOpenChange={setMergeDialogOpen}
        fromSelectionId={selection.id}
        fromSelectionName={selection.name || selection.id}
        skuCount={skuCount}
      />
    </Card>
  );
}
